import { Suspense } from 'react';
import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { AdminShell } from '../shell/admin-shell';
import { FlagsScreen } from '../screens/flags-screen';
import { TenantsScreen } from '../screens/tenants-screen';
import { ExperimentsScreen } from '../screens/experiments-screen';
import { OpsScreen } from '../screens/ops-screen';
import { AuditScreen } from '../screens/audit-screen';
import { VaultDetailScreen } from '../screens/vault-detail-screen';
import { CrawlsScreen } from '../screens/crawls-screen';
import { CrawlRunDetailScreen } from '../screens/crawl-run-detail-screen';
import { DataHealthScreen } from '../screens/data-health-screen';
import { LoadingContent } from '../screens/loading-content';
import { parseVaultListFilter } from '../screens/vault-list-filter';

/**
 * TanStack Router tree. The root route is the persistent `AdminShell`; content
 * renders through `<Outlet/>` inside a Suspense boundary so the shell stays put
 * while a screen's Relay query (and any cold start) resolves.
 */
const rootRoute = createRootRoute({
    component: () => (
        <AdminShell>
            <Suspense fallback={<LoadingContent />}>
                <Outlet />
            </Suspense>
        </AdminShell>
    ),
});

const flagsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: FlagsScreen,
});

const tenantsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/tenants',
    // `?vaults=complimentary` narrows the directory to live comps; anything else is dropped.
    validateSearch: (search: Record<string, unknown>): { vaults?: 'complimentary' } =>
        parseVaultListFilter(search) === 'complimentary' ? { vaults: 'complimentary' } : {},
    component: TenantsScreen,
});

const experimentsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/experiments',
    component: ExperimentsScreen,
});

const opsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/ops',
    component: OpsScreen,
});

const auditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/audit',
    component: AuditScreen,
});

const vaultDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/vaults/$vaultId',
    component: VaultDetailScreen,
});

const crawlsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/listings/crawls',
    component: CrawlsScreen,
});

const crawlRunDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/listings/crawls/$runId',
    component: CrawlRunDetailScreen,
});

const dataHealthRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/data/health',
    component: DataHealthScreen,
});

const routeTree = rootRoute.addChildren([
    flagsRoute,
    tenantsRoute,
    experimentsRoute,
    opsRoute,
    auditRoute,
    vaultDetailRoute,
    crawlsRoute,
    crawlRunDetailRoute,
    dataHealthRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}
